import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
} from '@mui/material';
import CustomBreadcrumbs from '../../../Components/custom_components/CustomBreadcrumbs/CustomBreadcrumbs';
import {
    get_roles_with_pagination
} from '../../../config/SettingsApis/rolesApis';
import {
    get_email_context_without_pagination,
    save_email_conext_mapping
} from '../../../config/SettingsApis/emailNotificationApis';
import { ErrorToast, SuccessToast } from '../../../utils/toast';
import ActionButton from '../../../Components/custom_components/CustomButtons/ActionButton/ActionButton';
import TextSkeleton from '../../../Components/Skeletons/TextSkeleton';

const EmailContextRoleMatrix = () => {
    const [rolesList, setRolesList] = useState([]);
    const [emailContextList, setEmailContextList] = useState([]);
    const [mapping, setMapping] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    //  Fetch roles
    const fetchRoles = async () => {
        try {
            const response = await get_roles_with_pagination({ status: 1 });
            if (response?.status === "success") {
                const transformed = response.data.data
                    ?.filter(role => role.RoleID !== 5)
                    .map(role => ({ label: role.Rolename, value: role.RoleID }));
                setRolesList(transformed);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    //  Fetch paginated email contexts and mapping
    const getEmailContextWithPagination = async () => {

        try {
            setIsLoading(true);
            const response = await get_email_context_without_pagination();

            if (response?.status === "success") {
                const data = response.data.data || response.data;

                const newContextList = data.map(ctx => ({
                    id: ctx.email_context_id,
                    context: ctx.email_context,
                }));

                const newMapping = {};
                data.forEach(ctx => {
                    ctx.roles?.forEach(role => {
                        if (!newMapping[role.role_id]) {
                            newMapping[role.role_id] = [];
                        }
                        newMapping[role.role_id].push(ctx.email_context_id);
                    });
                });

                // Ensure uniqueness
                const uniqueMapping = {};
                Object.entries(newMapping).forEach(([roleId, ctxIds]) => {
                    uniqueMapping[roleId] = [...new Set(ctxIds)];
                });

                setEmailContextList(newContextList);
                setMapping(uniqueMapping);
            } else {
                setEmailContextList([]);
            }
        } catch (error) {
            console.error('Error fetching email contexts:', error);
            setEmailContextList([]);
        } finally {
            setIsLoading(false);
        }
    };


    useEffect(() => {
        fetchRoles();
    }, []);

    useEffect(() => {
        getEmailContextWithPagination();
    }, [isSubmitting]);

    //  Handle checkbox
    const handleCheckboxChange = (roleId, contextId) => {
        setMapping(prev => {
            const current = prev[roleId] || [];
            const isSelected = current.includes(contextId);

            const updatedContexts = isSelected
                ? current.filter(id => id !== contextId)
                : [...current, contextId];

            return {
                ...prev,
                [roleId]: updatedContexts
            };
        });
    };

    //  Handle Submit
    const handleSubmit = async () => {
        setIsSubmitting(true);
        const selectedRoleIds = Object.keys(mapping)
            .filter(roleId => mapping[roleId]?.length > 0)
            .map(id => parseInt(id));

        const selectedContextIds = [
            ...new Set(Object.values(mapping).flat().map(id => parseInt(id)))
        ];

        const payload = {
            email_context_id: selectedContextIds,
            role_id: selectedRoleIds,
            email_is_enable: 1
        };

        try {
            const response = await save_email_conext_mapping(payload);
            if (response?.status === 'success') {
                SuccessToast(response?.message);
            } else {
                ErrorToast(response?.message)
            }
        } catch (error) {
            console.error("Error saving mapping:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const breadcrumbs = ['Settings', 'Email Assign']
    return (

        <>
            <div className='lookup-filter-container'>
                <div className='lookup-filter-sub-container'>
                    <div className='technology-lookup-search-left'>
                        {!isLoading ? (
                            <>
                                <div className="lookup-section-title">Email Assign</div>
                                <CustomBreadcrumbs breadcrumbs={breadcrumbs} />
                            </>
                        ) : (
                            <>
                                <TextSkeleton width="200px" fontSize="2rem" />
                                <TextSkeleton width="150px" fontSize="1.2rem" />
                            </>
                        )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div></div>
                        <div className='lookup-action-button-container'>
                            <Box mt={3} display="flex" justifyContent={"end"} gap={2}>
                                {!isLoading ? (
                                    <ActionButton
                                        onClick={handleSubmit}
                                        btnDisable={isSubmitting}
                                        label={'Save'}
                                        btnType="primary"
                                    />
                                ) : (
                                    <TextSkeleton width="100px" fontSize="3rem" />
                                )}
                            </Box>
                        </div>
                    </div>
                </div>
            </div>

            <Box p={2}>
                <Paper variant="outlined" sx={{ mt: 2, overflowX: 'auto' }}>
                    {!isLoading ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Role</strong></TableCell>
                                    {emailContextList?.map(ctx => (
                                        <TableCell key={ctx.id} align="center">
                                            <strong>{ctx.context}</strong>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rolesList?.map(role => (
                                    <TableRow key={role.value}>
                                        <TableCell>{role.label}</TableCell>
                                        {emailContextList?.map(ctx => (
                                            <TableCell key={ctx.id} align="center">
                                                <Checkbox
                                                    checked={mapping[role.value]?.includes(ctx.id) || false}
                                                    onChange={() => handleCheckboxChange(role.value, ctx.id)}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><TextSkeleton width="80px" /></TableCell>
                                    {Array(3).fill(0).map((_, i) => (
                                        <TableCell key={i} align="center">
                                            <TextSkeleton width="60px" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array(5).fill(0).map((_, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        <TableCell><TextSkeleton width="100px" /></TableCell>
                                        {Array(3).fill(0).map((_, cellIndex) => (
                                            <TableCell key={cellIndex} align="center">
                                                <TextSkeleton width="60px" />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Paper>

            </Box>
        </>
    );
};

export default EmailContextRoleMatrix;